import React, { useEffect, useState } from "react";
import { useForm } from 'react-hook-form';
import cx from 'classnames';
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { Typography } from "@mui/material";
import { ENV } from '../../../config/env';
import { useFirebase } from '../../../hooks/useFirebase';
import { withReducerLogger } from '../../../utils/debugReducer';
import './LoginForm.styles.scss';
import './Form.styles.scss';

type LoginProps = { onForgot: () => void }

type LoginInput = {
  email: string
  password: string
}

type LoginAction =
  | { type: 'loading' }
  | { type: 'error'; message: string }
  | { type: 'success' }

type LoginState = {
  loading: boolean;
  error?: string;
  success: boolean;
}

const reducer = (state: LoginState, action: LoginAction): LoginState => {
  switch (action.type) {
    case 'loading':
      return { ...state, loading: true, error: undefined };
    case 'error':
      return { ...state, loading: false, error: action.message };
    case 'success':
      return { ...state, loading: false, success: true };
    default:
      return state;
  }
}

export const LoginForm: React.FC<LoginProps> = ({ onForgot }) => {
  const qs = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const navigate = useNavigate();
  const firebaseApp = useFirebase();
  const auth = firebaseApp ? getAuth(firebaseApp) : null;

  const [initializing, setInitializing] = useState(true); // New state to track auth loading
  const [currentUser, setCurrentUser] = useState<User | null>(null); // Store current user

  useEffect(() => {
    if (!firebaseApp) return;

    const auth = getAuth(firebaseApp);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setInitializing(false); // Mark auth as initialized
      if (user) {
        navigate('/account/');
      }
    });

    return () => unsubscribe();
  }, [firebaseApp, navigate]);

  // Hooks should be defined unconditionally
  const { register, handleSubmit, formState: { errors } } = useForm<LoginInput>({
    defaultValues: { email: qs.get('email') || '' },
  });

  const [state, dispatch] = React.useReducer(ENV.isDevelopment ? withReducerLogger(reducer) : reducer,
    { loading: false, success: false }
  );

  if (!firebaseApp) {
    console.error("Firebase app is not initialized");
    return <p>Firebase is not initialized. Please try again later.</p>;
  }

  if (initializing) {
    return <p>Loading...</p>; // Show loading while checking auth
  }

  if (currentUser) {
    return <p>Loading...</p>; // Show loading while redirecting
  }

  const onSubmit = async (values: LoginInput) => {
    try {
      dispatch({ type: 'loading' });
      await setPersistence(auth!, browserSessionPersistence);
      await signInWithEmailAndPassword(auth!, values.email, values.password);
      dispatch({ type: 'success' });
      navigate('/account/');
    } catch (ex) {
      console.warn(ex);
      dispatch({
        type: 'error',
        message: 'Your email address or password is incorrect',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="login-form__container" noValidate>
      <Typography variant="h4" className="login-form__title" >Login</Typography>
      <p className="login-form__intro">
        Enter your details below to access your membership.
      </p>
      <div className="control">
        <input type="email" id="email" placeholder="Email address"
          className={cx('input', { '--error': errors.email })}
          {...register('email', {
            required: 'This is a required field',
            pattern: { value: /.+@.+\..+/, message: 'Please enter a valid email address' },
          })}
        />
        {errors.email && <p className="control__warning">{errors.email.message}</p>}
      </div>
      <div className="control">
        <input type="password" id="password" placeholder="Password"
          className={cx('input', { '--error': errors.password })}
          {...register('password', { required: 'This is a required field' })}
        />
        {errors.password && <p className="control__warning">{errors.password.message}</p>}
      </div>
      {state.error && <p className="control__warning">{state.error}</p>}
      <button className="login-form__action" type="submit" disabled={state.loading}>
        Sign in
      </button>
      <button className="login-form__forgot" type="button" onClick={onForgot} disabled={state.loading}>
        Forgot password
      </button>
    </form>
  );
};